import "./Report.css";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import config from "../../config";
import Button from 'react-bootstrap/Button';
import { Col, Container, Row, Form } from "react-bootstrap";
import SideNav from "../SideNav/SideNav";
import CO2ComparisonBarChart from "./Widgets/CO2ComparisonBar";
import CCEFullChart from "./Widgets/CCEFullChart";
import CCECostFullChart from "./Widgets/CCECostFullChart";
import CostComparisonBarChart from "./Widgets/CostComparisonBar";

const Report = () => {
    let { id } = useParams();
    let navigate = useNavigate();
    const [experiment, setExperiment] = useState({});

    const [design1CO2eq, setDesign1CO2eq] = useState([]);
    const [design2CO2eq, setDesign2CO2eq] = useState([]);
    const [design1CO2eq_median, setDesign1CO2eq_median] = useState([]);
    const [design2CO2eq_median, setDesign2CO2eq_median] = useState([]);

    const [design1Cost, setDesign1Cost] = useState([]);
    const [design2Cost, setDesign2Cost] = useState([]);
    const [design1Cost_median, setDesign1Cost_median] = useState([]);
    const [design2Cost_median, setDesign2Cost_median] = useState([]);

    // Chart visibility controls - auto show all at start
    const [showGHGCharts, setShowGHGCharts] = useState(true);
    const [showCostCharts, setShowCostCharts] = useState(true);

  useEffect(() => {
        axios.get(`${config.apiUrl}/api/experiment/${id}`)
        .then((response) => {
            setExperiment(response.data);
            console.log(response.data);
        })
        .catch((error) => {
            // Handle error
        });
    }, [id]);

    // === Auto-poll for results until fresh data is ready ===
    useEffect(() => {
        let attempts = 0;

        const pollForResults = setInterval(async () => {
            attempts++;
            try {
                const v = () => `${Date.now()}_${Math.random()}`;

                // --- GHG data ---
                const [res1, res2, res1m, res2m] = await Promise.all([
                    axios.get(`${config.apiUrl}/api/report/data/design1-co2eq/${id}?v=${v()}`),
                    axios.get(`${config.apiUrl}/api/report/data/design2-co2eq/${id}?v=${v()}`),
                    axios.get(`${config.apiUrl}/api/report/data/design1-co2eq-median/${id}?v=${v()}`),
                    axios.get(`${config.apiUrl}/api/report/data/design2-co2eq-median/${id}?v=${v()}`)
                ]);

                // --- COST data ---
                const [res3, res4, res3m, res4m] = await Promise.all([
                    axios.get(`${config.apiUrl}/api/report/data/design1-cost/${id}?v=${v()}`),
                    axios.get(`${config.apiUrl}/api/report/data/design2-cost/${id}?v=${v()}`),
                    axios.get(`${config.apiUrl}/api/report/data/design1-cost-median/${id}?v=${v()}`),
                    axios.get(`${config.apiUrl}/api/report/data/design2-cost-median/${id}?v=${v()}`)
                ]);

                const d1Ready =
                    res1.status === 200 &&
                    Array.isArray(res1.data.design1_co2eq) &&
                    res1.data.design1_co2eq.length > 0;
                const d2Ready =
                    res2.status === 200 &&
                    Array.isArray(res2.data.design2_co2eq) &&
                    res2.data.design2_co2eq.length > 0;
                const costReady =
                    res3.status === 200 &&
                    Array.isArray(res3.data.design1_cost) &&
                    res3.data.design1_cost.length > 0;

                if (d1Ready && d2Ready && costReady) {
                    clearInterval(pollForResults);
                    setDesign1CO2eq(res1.data.design1_co2eq);
                    setDesign2CO2eq(res2.data.design2_co2eq);
                    setDesign1CO2eq_median(res1m.data.design1_co2eq_median);
                    setDesign2CO2eq_median(res2m.data.design2_co2eq_median);
                    setDesign1Cost(res3.data.design1_cost);
                    setDesign2Cost(res4.data.design2_cost);
                    setDesign1Cost_median(res3m.data.design1_cost_median);
                    setDesign2Cost_median(res4m.data.design2_cost_median);
                } else {
                    console.log("Results not ready yet...");
                }
            } catch (err) {
                console.log("Polling error:", err.message);
            }

            if (attempts > 60) {
                clearInterval(pollForResults);
                console.warn("Stopped polling after 3 minutes without complete data.");
            }
        }, 3000);

        return () => clearInterval(pollForResults);
    }, [id]);

useEffect(() => {
    console.log("Updated design1CO2eq: ", design1CO2eq);
    console.log("Updated design2CO2eq: ", design2CO2eq);
  }, [design1CO2eq, design2CO2eq]);

useEffect(() => {
    console.log("Updated design1CO2eq_median: ", design1CO2eq_median);
    console.log("Updated design2CO2eq_median: ", design2CO2eq_median);
}, [design1CO2eq_median, design2CO2eq_median]);

useEffect(() => {
    console.log("Updated design1Cost: ", design1Cost);
    console.log("Updated design2Cost: ", design2Cost);
}, [design1Cost, design2Cost]);

useEffect(() => {
    console.log("Updated design1Cost_median: ", design1Cost_median);
    console.log("Updated design2Cost_median: ", design2Cost_median);
}, [design1Cost_median, design2Cost_median]);


const goToBasicContext = () => {
    setTimeout(() => {
        navigate(`/pavement-context-basic/${id}`);
    }, 100);
}

const goToDetailedContext = () => {
    setTimeout(() => {
        navigate(`/pavement-context-custom/${id}`);
    }, 100);
}

const goToPedegreeMatrix = () => {
    setTimeout(() => {
        navigate(`/pedegree-matrix/${id}?backref=pavement-design`);
    }, 100);
}

const goToConcreteEmbodiedEmission = () => {
    setTimeout(() => {
        navigate(`/asphalt-embodied-emission/${id}?backref=pavement-design`);
    }, 100);
}

const goToAsphaltEmbodiedEmission = () => {
    setTimeout(() => {
        navigate(`/asphalt-embodied-emission/${id}?backref=pavement-design`);
    }, 100);
}

const goToPavementDesign = () => {
    setTimeout(() => {
        navigate(`/pavement-design/${id}?backref=result`);
    }, 100);
}

return (
    <>
        <SideNav id={id} passed={["pavement-context-basic", "pavement-context-custom", "pedegree-matrix", "pavement-design", "concrete-embodied-emission", "asphalt-embodied-emission"]}
            goToBasicContext={goToBasicContext}
            goToDetailedContext={goToDetailedContext}
            goToAsphaltEmbodiedEmission={goToAsphaltEmbodiedEmission}
            goToConcreteEmbodiedEmission={goToConcreteEmbodiedEmission}
            goToPedegreeMatrix={goToPedegreeMatrix}
            goToPavementDesign={goToPavementDesign}
            goToResults={() => { }}
        />
        <Container>
        
        <Row>
        <Col>
            <div className="pcb__sub">
            <h3 className="pcb__sub-title">Experiment Context</h3>
            <div className="rep__box">
                <table className="rep__table">
                <thead>
                    <tr>
                    <td className="rep__lable">Experiment</td>
                    <td className="rep__lable">State</td>
                    <td className="rep__lable">Specification</td>
                    <td className="rep__lable">Design Life</td>
                    <td className="rep__lable">Iterations</td>
                    <td className="rep__lable">Analysis Period</td>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                    <td className="rep__value">{experiment?.title}</td>
                    <td className="rep__value">{experiment?.context?.state}</td>
                    <td className="rep__value">{experiment?.design_specification}</td>
                    <td className="rep__value">{experiment?.design_life}</td>
                    <td className="rep__value">{experiment?.number_of_iterations}</td>
                    <td className="rep__value">{experiment?.analysisPeriod}</td>
                    </tr>
                </tbody>
                </table>
            </div>
            </div>
        </Col>
        </Row>

        <Row>
        <Col>
            <div className="pcb__sub">
            <h3 className="pcb__sub-title">{experiment?.design1?.title}</h3>
            <div className="rep__box">
                <table className="rep__table">
                <tbody>
                    <tr>
                    <td className="rep__lable">Surface</td>
                    <td className="rep__value">{experiment?.design1?.surface?.type}</td>
                    <td className="rep__lable">Min thickness</td>
                    <td className="rep__value">{experiment?.design1?.surface?.thickness?.min}</td>
                    <td className="rep__lable">Max thickness</td>
                    <td className="rep__value">{experiment?.design1?.surface?.thickness?.max}</td>
                    </tr>
                    <tr>
                    <td className="rep__lable">Base</td>
                    <td className="rep__value">{experiment?.design1?.base?.type}</td>
                    <td className="rep__lable">Min thickness</td>
                    <td className="rep__value">{experiment?.design1?.base?.thickness?.min}</td>
                    <td className="rep__lable">Max thickness</td>
                    <td className="rep__value">{experiment?.design1?.base?.thickness?.max}</td>
                    </tr>
                    <tr>
                    <td></td>
                    <td></td>
                    <td className="rep__lable">K value min</td>
                    <td className="rep__value">{parseFloat(experiment?.design1?.kValue?.min ?? 0).toFixed(2)}</td>
                    <td className="rep__lable">K-Value max</td>
                    <td className="rep__value">{parseFloat(experiment?.design1?.kValue?.max ?? 0).toFixed(2)}</td>
                    </tr>
                </tbody>
                </table>
            </div>
            </div>
        </Col>
        <Col>
            <div className="pcb__sub">
            <h3 className="pcb__sub-title">{experiment?.design2?.title}</h3>
            <div className="rep__box">
                <table className="rep__table">
                <tbody>
                    <tr>
                    <td className="rep__lable">Surface</td>
                    <td className="rep__value">{experiment?.design2?.surface?.type}</td>
                    <td className="rep__lable">Min thickness</td>
                    <td className="rep__value">{experiment?.design2?.surface?.thickness?.min}</td>
                    <td className="rep__lable">Max thickness</td>
                    <td className="rep__value">{experiment?.design2?.surface?.thickness?.max}</td>
                    </tr>
                    <tr>
                    <td className="rep__lable">Base</td>
                    <td className="rep__value">{experiment?.design2?.base?.type}</td>
                    <td className="rep__lable">Min thickness</td>
                    <td className="rep__value">{experiment?.design2?.base?.thickness?.min}</td>
                    <td className="rep__lable">Max thickness</td>
                    <td className="rep__value">{experiment?.design2?.base?.thickness?.max}</td>
                    </tr>
                    <tr>
                    <td></td>
                    <td></td>
                    <td className="rep__lable">K value min</td>
                    <td className="rep__value">{parseFloat(experiment?.design2?.kValue?.min ?? 0).toFixed(2)}</td>
                    <td className="rep__lable">K-Value max</td>
                    <td className="rep__value">{parseFloat(experiment?.design2?.kValue?.max ?? 0).toFixed(2)}</td>
                    </tr>
                </tbody>
                </table>
            </div>
            </div>
        </Col>
        </Row>

        {/* Results Visualization */}
        <Row>
        <Col>
            <div className="pcb__sub">
            <h3 className="pcb__sub-title">Results Visualization</h3>
            <div className="rep__box" style={{ padding: "15px" }}>
                <Form>
                <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
                    <Form.Check
                    type="checkbox"
                    id="show-ghg-charts"
                    label="Display of LCA Results (GHG Emission Charts)"
                    checked={showGHGCharts}
                    onChange={(e) => setShowGHGCharts(e.target.checked)}
                    />
                    <Form.Check
                    type="checkbox"
                    id="show-cost-charts"
                    label="Display of LCCA Results (Cost Charts)"
                    checked={showCostCharts}
                    onChange={(e) => setShowCostCharts(e.target.checked)}
                    />
                </div>
                </Form>
            </div>
            </div>
        </Col>
        </Row>

        {/* GHG Charts Row */}
        {showGHGCharts && (
        <Row>
        <Col>
            <div style={{width: '570px', margin: '20px 0px 0px 0px'}}>
            <CCEFullChart
                design1CO2eq={design1CO2eq}
                design2CO2eq={design2CO2eq}
                design1CO2eq_median={design1CO2eq_median}
                design2CO2eq_median={design2CO2eq_median}
                analysisPeriod={experiment.analysisPeriod}
                design1title = {experiment?.design1?.title}
                design2title = {experiment?.design2?.title}
            />
            </div>
        </Col>
        <Col>
            <div style={{width: '480px', margin: '20px 0px 0px 0px'}}>
            {design1CO2eq.length > 0 ? (
                <CO2ComparisonBarChart
                design1CO2eq_median={design1CO2eq_median}
                design2CO2eq_median={design2CO2eq_median}
                analysisPeriod={experiment.analysisPeriod}
                design1title = {experiment?.design1?.title}
                design2title = {experiment?.design2?.title}
                />
            ) : (
                <div style={{ 
                display: "flex", 
                justifyContent: "center", 
                alignItems: "center", 
                height: "300px",
                backgroundColor: "#f8f9fa",
                border: "1px solid #dee2e6",
                borderRadius: "5px"
                }}>
                <p>Loading GHG data...</p>
                </div>
            )}
            </div>
        </Col>
        {/* Info panel on the right - only shows with GHG charts */}
        <Col>
            <div className="info__panel">
            <ul className="infolist">
                <li><Link className="info__link" to="/info/albedo">Albedo</Link></li>
                <li><Link className="info__link" to="/info/carbon-uptake">Carbon Uptake</Link></li>
                <li><Link className="info__link" to="/info/end-of-life">End of Life</Link></li>
                <li><Link className="info__link" to="/info/initial-emission">Initial Emission</Link></li>
                <li><Link className="info__link" to="/info/mnr">M&R</Link></li>
                <li><Link className="info__link" to="/info/pvi-deflection">PVI Deflection</Link></li>
                <li><Link className="info__link" to="/info/pvi-roughness">PVI Roughness</Link></li>
                <li><Link className="info__link" to="/info/iri-asphalt">IRI for Asphalt Pavement</Link></li>
                <li><Link className="info__link" to="/info/iri-rigid">IRI for Concrete Pavement</Link></li>
            </ul>
            </div>
        </Col>
        </Row>
        )}

        {/* Cost Charts Row */}
        {showCostCharts && (
        <Row>
        <Col>
            <div style={{width: '570px', margin: '20px 0px 0px 0px'}}>
            <CCECostFullChart
                design1Cost={design1Cost}
                design2Cost={design2Cost}
                design1Cost_median={design1Cost_median}
                design2Cost_median={design2Cost_median}
                analysisPeriod={experiment.analysisPeriod}
                design1title = {experiment?.design1?.title}
                design2title = {experiment?.design2?.title}
            />
            </div>
        </Col>
        <Col>
            <div style={{width: '480px', margin: '20px 0px 0px 0px'}}>
            {design1Cost.length > 0 ? (
                <CostComparisonBarChart
                design1Cost_median={design1Cost_median}
                design2Cost_median={design2Cost_median}
                analysisPeriod={experiment.analysisPeriod}
                design1title = {experiment?.design1?.title}
                design2title = {experiment?.design2?.title}
                />
            ) : (
                <div style={{ 
                display: "flex", 
                justifyContent: "center", 
                alignItems: "center", 
                height: "300px",
                backgroundColor: "#f8f9fa",
                border: "1px solid #dee2e6",
                borderRadius: "5px"
                }}>
                <p>Loading cost data...</p>
                </div>
            )}
            </div>
        </Col>
        {/* Show info panel on right only if GHG charts are NOT showing (to avoid duplicate) */}
        {!showGHGCharts && (
        <Col>
            <div className="info__panel">
            <ul className="infolist">
                <li><Link className="info__link" to="/info/albedo">Albedo</Link></li>
                <li><Link className="info__link" to="/info/carbon-uptake">Carbon Uptake</Link></li>
                <li><Link className="info__link" to="/info/end-of-life">End of Life</Link></li>
                <li><Link className="info__link" to="/info/initial-emission">Initial Emission</Link></li>
                <li><Link className="info__link" to="/info/mnr">M&R</Link></li>
                <li><Link className="info__link" to="/info/pvi-deflection">PVI Deflection</Link></li>
                <li><Link className="info__link" to="/info/pvi-roughness">PVI Roughness</Link></li>
                <li><Link className="info__link" to="/info/iri-asphalt">IRI for Asphalt Pavement</Link></li>
                <li><Link className="info__link" to="/info/iri-rigid">IRI for Concrete Pavement</Link></li>
            </ul>
            </div>
        </Col>
        )}
        </Row>
        )}

        <Row>
            <div className="pcb__box-footer" style={{ display: "flex", gap: "15px" }}>
                <Button variant="primary" href={`/detailed-report-co2/${id}`}>LCA Results (GHG Emission) Detailed Report</Button>
                <Button variant="primary" href={`/detailed-report-cost/${id}`}>LCCA Results (Cost) Detailed Report</Button>
            </div>
        </Row>
    </Container>
    </>
    );
};

export default Report;